import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useSafeSelectHandler } from '@/hooks/useResizeObserverFix';
import { useLevels } from '@/hooks/useLevels';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { SafeSelect as Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/SafeSelect';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Icons
import { 
  UserPlus,
  GraduationCap,
  Users,
  User,
  Mail,
  Phone,
  Lock,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserCreation = ({ user }) => {
  const { levels, loading: levelsLoading } = useLevels();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('student');

  // Form states for different user types
  const [studentForm, setStudentForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    level: ''
  });

  const [teacherForm, setTeacherForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  const [parentForm, setParentForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  const [treasurerForm, setTreasurerForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  const [secretaryForm, setSecretaryForm] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  // Supprimé: fetchLevels() maintenant géré par le hook useLevels

  // Handler sécurisé pour les changements de niveau utilisant requestAnimationFrame
  const handleLevelChange = useSafeSelectHandler(
    useCallback((level) => {
      setStudentForm(prev => ({ ...prev, level: level }));
    }, [])
  );

  // Supprimé: fetchLevels() maintenant géré par le hook useLevels

  const handleCreateUser = async (userType, formData) => {
    setLoading(true);
    try {
      const userData = {
        ...formData,
        role: userType.toUpperCase(),
        level: userType === 'student' ? formData.level : undefined
      };

      const response = await axios.post(`${API}/admin/create-user`, userData);
      
      toast.success(response.data.message);
      
      // Reset form
      if (userType === 'student') {
        setStudentForm({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          phone: '',
          level: ''
        });
      } else if (userType === 'teacher') {
        setTeacherForm({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          phone: ''
        });
      } else if (userType === 'parent') {
        setParentForm({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          phone: ''
        });
      } else if (userType === 'treasurer') {
        setTreasurerForm({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          phone: ''
        });
      } else if (userType === 'secretary') {
        setSecretaryForm({
          email: '',
          password: '',
          first_name: '',
          last_name: '',
          phone: ''
        });
      }

    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(errorMsg);
      console.error('Error creating user:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    return password.length >= 6;
  };

  const validateForm = (formData, userType) => {
    if (!formData.email || !validateEmail(formData.email)) {
      toast.error('Veuillez saisir une adresse email valide');
      return false;
    }

    if (!formData.password || !validatePassword(formData.password)) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }

    if (!formData.first_name || !formData.last_name) {
      toast.error('Veuillez saisir le prénom et le nom');
      return false;
    }

    if (userType === 'student' && !formData.level) {
      toast.error('Veuillez sélectionner un niveau pour l\'étudiant');
      return false;
    }

    return true;
  };

  // Autoriser ADMIN, PRINCIPAL, et SECRETARY
  // Pour les autres rôles, vérifier les permissions granulaires si nécessaire
  const allowedRoles = ['ADMIN', 'PRINCIPAL', 'SECRETARY'];
  
  if (!allowedRoles.includes(user.role)) {
    return (
      <div className="p-6">
        <Card className="card">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Accès refusé</h3>
              <p className="text-gray-600">
                Vous n'avez pas les permissions nécessaires pour créer des utilisateurs.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <UserPlus className="w-6 h-6 mr-2 text-blue-600" />
            Création d'utilisateurs
          </h1>
          <p className="text-gray-600 mt-1">
            Créez des comptes pour les étudiants, enseignants et parents
          </p>
        </div>
      </div>

      {/* User Creation Tabs */}
      <Card className="card">
        <CardHeader>
          <CardTitle>Nouveau compte utilisateur</CardTitle>
          <CardDescription>
            Sélectionnez le type d'utilisateur à créer et remplissez les informations
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-5">
              <TabsTrigger value="student" className="flex items-center">
                <GraduationCap className="w-4 h-4 mr-2" />
                Étudiant
              </TabsTrigger>
              <TabsTrigger value="teacher" className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                Enseignant
              </TabsTrigger>
              <TabsTrigger value="parent" className="flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Parent
              </TabsTrigger>
              <TabsTrigger value="treasurer" className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                Trésorier
              </TabsTrigger>
              <TabsTrigger value="secretary" className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                Secrétaire
              </TabsTrigger>
            </TabsList>

            {/* Student Creation Form */}
            <TabsContent value="student" className="space-y-4 mt-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <GraduationCap className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Création d'étudiant</p>
                    <p>Un profil étudiant sera automatiquement créé avec un numéro d'étudiant généré.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (validateForm(studentForm, 'student')) {
                  handleCreateUser('student', studentForm);
                }
              }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="student_first_name">Prénom *</Label>
                    <Input
                      id="student_first_name"
                      value={studentForm.first_name}
                      onChange={(e) => setStudentForm({ ...studentForm, first_name: e.target.value })}
                      placeholder="Prénom de l'étudiant"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="student_last_name">Nom *</Label>
                    <Input
                      id="student_last_name"
                      value={studentForm.last_name}
                      onChange={(e) => setStudentForm({ ...studentForm, last_name: e.target.value })}
                      placeholder="Nom de l'étudiant"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="student_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="student_email"
                      type="email"
                      value={studentForm.email}
                      onChange={(e) => setStudentForm({ ...studentForm, email: e.target.value })}
                      placeholder="email@exemple.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="student_password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <PasswordInput
                      id="student_password"
                      value={studentForm.password}
                      onChange={(e) => setStudentForm({ ...studentForm, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="student_phone">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="student_phone"
                      type="tel"
                      value={studentForm.phone}
                      onChange={(e) => setStudentForm({ ...studentForm, phone: e.target.value })}
                      placeholder="+224 6XX XX XX XX"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="student_level">Niveau *</Label>
                  <Select value={studentForm.level} onValueChange={handleLevelChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un niveau" />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((levelInfo) => (
                        <SelectItem key={levelInfo.level} value={levelInfo.level}>
                          {levelInfo.name} - {levelInfo.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {levels.length === 0 && (
                    <p className="text-xs text-orange-600">
                      Aucun niveau disponible. Contactez l'administrateur.
                    </p>
                  )}
                </div>

                <LoadingButton 
                  type="submit" 
                  className="w-full btn-primary" 
                  loading={loading}
                  loadingText="Création de l'étudiant..."
                >
                  <GraduationCap className="w-4 h-4 mr-2" />
                  Créer l'étudiant
                </LoadingButton>
              </form>
            </TabsContent>

            {/* Teacher Creation Form */}
            <TabsContent value="teacher" className="space-y-4 mt-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <User className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Création d'enseignant</p>
                    <p>L'enseignant pourra être assigné aux classes après la création du compte.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (validateForm(teacherForm, 'teacher')) {
                  handleCreateUser('teacher', teacherForm);
                }
              }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="teacher_first_name">Prénom *</Label>
                    <Input
                      id="teacher_first_name"
                      value={teacherForm.first_name}
                      onChange={(e) => setTeacherForm({ ...teacherForm, first_name: e.target.value })}
                      placeholder="Prénom de l'enseignant"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="teacher_last_name">Nom *</Label>
                    <Input
                      id="teacher_last_name"
                      value={teacherForm.last_name}
                      onChange={(e) => setTeacherForm({ ...teacherForm, last_name: e.target.value })}
                      placeholder="Nom de l'enseignant"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="teacher_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="teacher_email"
                      type="email"
                      value={teacherForm.email}
                      onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })}
                      placeholder="email@exemple.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="teacher_password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <PasswordInput
                      id="teacher_password"
                      value={teacherForm.password}
                      onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="teacher_phone">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="teacher_phone"
                      type="tel"
                      value={teacherForm.phone}
                      onChange={(e) => setTeacherForm({ ...teacherForm, phone: e.target.value })}
                      placeholder="+224 6XX XX XX XX"
                      className="pl-10"
                    />
                  </div>
                </div>

                <LoadingButton 
                  type="submit" 
                  className="w-full btn-primary"
                  loading={loading}
                  loadingText="Création de l'enseignant..."
                >
                  <User className="w-4 h-4 mr-2" />
                  Créer l'enseignant
                </LoadingButton>
              </form>
            </TabsContent>

            {/* Parent Creation Form */}
            <TabsContent value="parent" className="space-y-4 mt-6">
              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <Users className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div className="text-sm text-purple-800">
                    <p className="font-medium mb-1">Création de parent</p>
                    <p>Le parent pourra être lié aux étudiants via la gestion des relations parent-enfant.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (validateForm(parentForm, 'parent')) {
                  handleCreateUser('parent', parentForm);
                }
              }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="parent_first_name">Prénom *</Label>
                    <Input
                      id="parent_first_name"
                      value={parentForm.first_name}
                      onChange={(e) => setParentForm({ ...parentForm, first_name: e.target.value })}
                      placeholder="Prénom du parent"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="parent_last_name">Nom *</Label>
                    <Input
                      id="parent_last_name"
                      value={parentForm.last_name}
                      onChange={(e) => setParentForm({ ...parentForm, last_name: e.target.value })}
                      placeholder="Nom du parent"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="parent_email"
                      type="email"
                      value={parentForm.email}
                      onChange={(e) => setParentForm({ ...parentForm, email: e.target.value })}
                      placeholder="email@exemple.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent_password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <PasswordInput
                      id="parent_password"
                      value={parentForm.password}
                      onChange={(e) => setParentForm({ ...parentForm, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent_phone">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="parent_phone"
                      type="tel"
                      value={parentForm.phone}
                      onChange={(e) => setParentForm({ ...parentForm, phone: e.target.value })}
                      placeholder="+224 6XX XX XX XX"
                      className="pl-10"
                    />
                  </div>
                </div>

                <LoadingButton 
                  type="submit" 
                  className="w-full btn-primary"
                  loading={loading}
                  loadingText="Création du parent..."
                >
                  <Users className="w-4 h-4 mr-2" />
                  Créer le parent
                </LoadingButton>
              </form>
            </TabsContent>

            {/* Treasurer Creation Form */}
            <TabsContent value="treasurer" className="space-y-4 mt-6">
              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <User className="w-5 h-5 text-green-600 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Création de trésorier</p>
                    <p>Le trésorier aura accès à la gestion financière et à la facturation.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (validateForm(treasurerForm, 'treasurer')) {
                  handleCreateUser('treasurer', treasurerForm);
                }
              }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="treasurer_first_name">Prénom *</Label>
                    <Input
                      id="treasurer_first_name"
                      value={treasurerForm.first_name}
                      onChange={(e) => setTreasurerForm({ ...treasurerForm, first_name: e.target.value })}
                      placeholder="Prénom du trésorier"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="treasurer_last_name">Nom *</Label>
                    <Input
                      id="treasurer_last_name"
                      value={treasurerForm.last_name}
                      onChange={(e) => setTreasurerForm({ ...treasurerForm, last_name: e.target.value })}
                      placeholder="Nom du trésorier"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="treasurer_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="treasurer_email"
                      type="email"
                      value={treasurerForm.email}
                      onChange={(e) => setTreasurerForm({ ...treasurerForm, email: e.target.value })}
                      placeholder="email@exemple.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="treasurer_password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <PasswordInput
                      id="treasurer_password"
                      value={treasurerForm.password}
                      onChange={(e) => setTreasurerForm({ ...treasurerForm, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="treasurer_phone">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="treasurer_phone"
                      type="tel"
                      value={treasurerForm.phone}
                      onChange={(e) => setTreasurerForm({ ...treasurerForm, phone: e.target.value })}
                      placeholder="+224 6XX XX XX XX"
                      className="pl-10"
                    />
                  </div>
                </div>

                <LoadingButton 
                  type="submit" 
                  className="w-full btn-primary"
                  loading={loading}
                  loadingText="Création du trésorier..."
                >
                  <User className="w-4 h-4 mr-2" />
                  Créer le trésorier
                </LoadingButton>
              </form>
            </TabsContent>

            {/* Secretary Creation Form */}
            <TabsContent value="secretary" className="space-y-4 mt-6">
              <div className="bg-indigo-50 p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <User className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div className="text-sm text-indigo-800">
                    <p className="font-medium mb-1">Création de secrétaire</p>
                    <p>Le secrétaire aura accès à la gestion des élèves, parents, absences et notes.</p>
                  </div>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (validateForm(secretaryForm, 'secretary')) {
                  handleCreateUser('secretary', secretaryForm);
                }
              }} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="secretary_first_name">Prénom *</Label>
                    <Input
                      id="secretary_first_name"
                      value={secretaryForm.first_name}
                      onChange={(e) => setSecretaryForm({ ...secretaryForm, first_name: e.target.value })}
                      placeholder="Prénom du secrétaire"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="secretary_last_name">Nom *</Label>
                    <Input
                      id="secretary_last_name"
                      value={secretaryForm.last_name}
                      onChange={(e) => setSecretaryForm({ ...secretaryForm, last_name: e.target.value })}
                      placeholder="Nom du secrétaire"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secretary_email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="secretary_email"
                      type="email"
                      value={secretaryForm.email}
                      onChange={(e) => setSecretaryForm({ ...secretaryForm, email: e.target.value })}
                      placeholder="email@exemple.com"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secretary_password">Mot de passe *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 z-10" />
                    <PasswordInput
                      id="secretary_password"
                      value={secretaryForm.password}
                      onChange={(e) => setSecretaryForm({ ...secretaryForm, password: e.target.value })}
                      placeholder="Minimum 6 caractères"
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="secretary_phone">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="secretary_phone"
                      type="tel"
                      value={secretaryForm.phone}
                      onChange={(e) => setSecretaryForm({ ...secretaryForm, phone: e.target.value })}
                      placeholder="+224 6XX XX XX XX"
                      className="pl-10"
                    />
                  </div>
                </div>

                <LoadingButton 
                  type="submit" 
                  className="w-full btn-primary"
                  loading={loading}
                  loadingText="Création du secrétaire..."
                >
                  <User className="w-4 h-4 mr-2" />
                  Créer le secrétaire
                </LoadingButton>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserCreation;